import React, { useRef, useState } from "react";
import { Upload, Loader2, AlertTriangle, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/lib/testIds";

/**
 * PDFImport — file drag/drop + upload. Talks to the modernized backend
 * endpoint POST /api/cruises/import-preview which returns an ephemeral
 * ExtractionPreview (no DB writes). On success, `onExtracted` is
 * invoked with `{ filename, extracted, resolved_line_id,
 * resolved_ship_id, line_id_candidates, ship_id_candidates }`.
 *
 * Restores PO-approved PDF drag-and-drop UX (2026-03-06). MWO-003
 * compliant — no finance_items writes, no GridFS persistence.
 */
export default function PDFImport({ onExtracted, onCancel, onFallbackManual }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [pendingFileName, setPendingFileName] = useState(null);
  const inputRef = useRef(null);

  const startUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a PDF file.");
      return;
    }
    setError(null);
    setPendingFileName(file.name);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/cruises/import-preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onExtracted(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  };

  if (uploading) {
    return (
      <div className="px-6 py-12 text-center" data-testid={TESTIDS.createCruisePDFUploading}>
        <Loader2 className="w-10 h-10 animate-spin text-amber-600 mx-auto" />
        <div className="font-display text-[18px] font-bold mt-4">Reading your confirmation…</div>
        <div className="text-[13px] text-slate-500 mt-1 inline-flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> {pendingFileName || "your PDF"} · GPT-5.2 is extracting the details.
        </div>
        <div className="text-[12px] text-slate-400 mt-3">This usually takes 5–15 seconds.</div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8" data-testid={TESTIDS.createCruisePDF}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full flex flex-col items-center justify-center gap-3 p-10 rounded-2xl border-2 border-dashed transition-all ${
          dragOver
            ? "border-amber-500 bg-amber-50"
            : "border-slate-300 hover:border-amber-400 hover:bg-amber-50/40"
        }`}
        data-testid={TESTIDS.createCruisePDFDropzone}
      >
        <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center">
          <Upload className="w-6 h-6" />
        </div>
        <div className="font-display text-[17px] font-bold text-slate-900">
          Drop your booking PDF here
        </div>
        <div className="text-[13px] text-slate-500 text-center max-w-md">
          Or click to browse. Vacations To Go, Royal Caribbean, Carnival, Princess, and most cruise-line confirmations are supported.
        </div>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        data-testid={TESTIDS.createCruisePDFFile}
        onChange={(e) => startUpload(e.target.files?.[0])}
      />

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-[13px]" data-testid={TESTIDS.createCruisePDFError}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Extraction failed</div>
            <div className="mt-0.5">{error}</div>
            <button
              type="button"
              onClick={onFallbackManual}
              className="mt-2 text-[12.5px] font-semibold text-amber-700 hover:text-amber-900"
              data-testid={TESTIDS.createCruisePDFFallback}
            >
              Enter cruise details manually instead →
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-semibold text-slate-500 hover:text-slate-700"
          data-testid={TESTIDS.createCruisePDFBack}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onFallbackManual}
          className="text-[13px] font-semibold text-slate-600 hover:text-slate-900"
        >
          Rather type it in?
        </button>
      </div>
    </div>
  );
}
